import {
  CreatePotentialTripBulkInput,
  CreatePotentialTripBulkResponse,
  PotentialTrip,
  TripKindEnum,
  TripTypeEnum,
} from '@/models/gen/graphql';
import { handleError, validateObj } from '@/utils/custom';

import { CreatePotentialTripBulkDocument } from '@/api/queries';
import { Datetime } from '@/utils';
import { GraphApiResponse } from '@/api/core';
import createGraphApiHook from '@/hooks/createGraphApiHook';
import graphApi from '@/api';

export const validatePotentialTrip = validateObj({
  airportCode: (val: string): string => val,
  attendants: (val: string): number => parseInt(`${val || 0}`) || 0,
  bufferMinutes: (val: number): number => val || 0,
  doLocationId: (val: string): string => val,
  flightNumber: (val: string): number => parseInt(`${val || 0}`) || 0,
  fromManifest: (val: number): number => (val ? 1 : 0),
  importId: (val: string): string => val,
  kind: (val: string): string => val,
  loopName: (val: string): string => val,
  payerProviderId: (val: string): string => val,
  pilots: (val: string): number => parseInt(`${val || 0}`) || 0,
  puLocationId: (val: string): string => val,
  scheduled: (val: string): string => val,
  servicerIataAirlineCode: (val: string): string => val || '',
  split: (val: number): number => val || 0,
  tripId: (val: string): string => val,
  crewId: (val: string): number => parseInt(`${val || 0}`) || 0,
  firstName: (val: string): string => val,
  lastName: (val: string): string => val,
});

const keys = [
  'airportCode',
  'payerProviderId',
  'providerId',
  'servicerIataAirlineCode',
  'puLocationId',
  'doLocationId',
  'flightNumber',
  'attendants',
  'pilots',
  'type',
  'kind',
  'status',
  'crewId',
  'firstName',
  'lastName',
];

export const createPotentialTripBulkValidator = (trip: any): any => {
  const validateKey = (key: string, val: any): any => {
    let isValid = undefined;
    const message = '';
    switch (key) {
      case 'airportCode': {
        isValid = !!val;
        break;
      }
      case 'payerProviderId': {
        isValid = !!val;
        break;
      }
      case 'servicerIataAirlineCode': {
        isValid = !!val;
        break;
      }
      case 'puLocationId': {
        isValid = !!val;
        break;
      }
      case 'doLocationId': {
        isValid = !!val;
        break;
      }
      case 'scheduled': {
        isValid = !!trip?.scheduledDays?.length || !!new Datetime(val || '').asDayjs().isValid();
        break;
      }
      case 'flightNumber': {
        isValid = val !== '' && Number(val) >= 0 && Number(val) <= 9999;
        break;
      }
      case 'type': {
        isValid = Object.values(TripTypeEnum).includes(val);
        break;
      }
      case 'kind': {
        isValid = Object.values(TripKindEnum).includes(val);
        break;
      }
      case 'crewId':
      case 'firstName':
      case 'lastName': {
        isValid = undefined;
        break;
      }
    }
    return { valid: isValid, message };
  };
  const result = {};
  keys.forEach((key: string): any => (result[key] = validateKey(key, trip?.[key])));
  return result;
};

type CreatePotentialTripBulkGraphApiResponse = GraphApiResponse<typeof CreatePotentialTripBulkDocument>;

const title = 'Create Potential Trip';

const createPotentialTripBulkResponseSelector = (res: CreatePotentialTripBulkGraphApiResponse): CreatePotentialTripBulkResponse =>
  !res.errors ? (res?.createPotentialTripBulk as CreatePotentialTripBulkResponse) : undefined;

const [runCreatePotentialTripBulk] = graphApi(CreatePotentialTripBulkDocument, {
  onError: (res: CreatePotentialTripBulkGraphApiResponse): void => handleError(res, { notification: { title } }),
});

const createPotentialTripBulk = async (...trips: PotentialTrip[]): Promise<CreatePotentialTripBulkResponse> => {
  const input: CreatePotentialTripBulkInput = {
    values: trips.map((trip) => ({ ...trip, tripId: '' })),
  };

  const res = await runCreatePotentialTripBulk({ input });
  const selected = createPotentialTripBulkResponseSelector(res);

  return selected;
};

export const useCreatePotentialTripBulk = createGraphApiHook(createPotentialTripBulk);

export default createPotentialTripBulk;
